<template>
  <div>
    <Nav></Nav>
    <b-container fluid class="mt-3">
      <!-- Header -->
      <b-row>
        <b-col>
          <h1>Ubicaciones de Inventario</h1>
        </b-col>
      </b-row>

      <!-- Action Buttons -->
      <b-row class="mb-3">
        <b-col cols="12" md="auto">
          <b-button
            variant="success"
            size="sm"
            @click="openCreateModal">
            Crear Ubicación
          </b-button>
        </b-col>
        <b-col cols="12" md="auto">
          <b-button
            variant="primary"
            size="sm"
            @click="getLocations">
            Actualizar
          </b-button>
        </b-col>
      </b-row>

      <!-- Search -->
      <b-row>
        <b-col>
          <b-form-group label="Buscar:">
            <b-form-input
              type="search"
              v-model="search"
              debounce="500"
              placeholder="Buscar por nombre, código de barras o comentario...">
            </b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Record Count -->
      <b-row>
        <b-col>
          Hay {{ filteredLocations.length }} ubicaciones
        </b-col>
      </b-row>

      <!-- Table -->
      <b-row>
        <b-col>
          <b-table
            :filter="search"
            striped
            hover
            small
            :items="locations"
            :fields="locationFields"
            :per-page="byPage"
            :current-page="page"
            responsive
            :busy="loading">
            <template #table-busy>
              <div class="text-center text-danger my-2">
                <b-spinner class="align-middle"></b-spinner>
                <strong>Cargando...</strong>
              </div>
            </template>
            <template #cell(type_usage)="data">
              <b-badge :variant="getTypeVariant(data.item.type_usage)">
                {{ getTypeLabel(data.item.type_usage) }}
              </b-badge>
            </template>
            <template #cell(status)="data">
              <b-badge :variant="data.item.status ? 'success' : 'danger'">
                {{ data.item.status ? 'Activo' : 'Inactivo' }}
              </b-badge>
            </template>
            <template #cell(is_scrap_location)="data">
              <b-icon-check-circle-fill
                v-if="data.item.is_scrap_location"
                variant="success">
              </b-icon-check-circle-fill>
            </template>
            <template #cell(is_return_location)="data">
              <b-icon-check-circle-fill
                v-if="data.item.is_return_location"
                variant="info">
              </b-icon-check-circle-fill>
            </template>
            <template #cell(is_replenish_location)="data">
              <b-icon-check-circle-fill
                v-if="data.item.is_replenish_location"
                variant="warning">
              </b-icon-check-circle-fill>
            </template>
            <template #cell(buttons)="data">
              <b-button-group size="sm">
                <b-button
                  variant="info"
                  @click="$router.push('/stock-location/' + data.item.id)">
                  Ver
                </b-button>
                <b-button
                  variant="warning"
                  @click="openEditModal(data.item)">
                  Editar
                </b-button>
                <b-button
                  variant="danger"
                  @click="confirmDelete(data.item)">
                  Eliminar
                </b-button>
              </b-button-group>
            </template>
          </b-table>
        </b-col>
      </b-row>

      <!-- Pagination -->
      <b-row>
        <b-col>
          <b-pagination
            v-model="page"
            :total-rows="filteredLocations.length"
            :per-page="byPage"
            aria-controls="locations-table">
          </b-pagination>
        </b-col>
      </b-row>

      <!-- Create/Edit Modal -->
      <b-modal
        id="modal-location"
        :title="isEditMode ? 'Editar Ubicación' : 'Nueva Ubicación'"
        :hide-footer="true"
        size="lg"
        @hidden="resetForm">
        <b-form @submit.prevent="saveLocation">
          <b-row>
            <b-col lg="6" cols="12">
              <b-form-group
                label="Nombre*"
                description="Nombre de la ubicación">
                <b-form-input
                  v-model="formLocation.name"
                  required
                  placeholder="Ej: Almacén Principal">
                </b-form-input>
              </b-form-group>
            </b-col>
            <b-col lg="6" cols="12">
              <b-form-group
                label="Nombre Completo"
                description="Nombre completo o ruta">
                <b-form-input
                  v-model="formLocation.complete_name"
                  placeholder="Ej: Almacén / Almacén Principal">
                </b-form-input>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col lg="6" cols="12">
              <b-form-group
                label="Tipo de Ubicación*"
                description="Tipo de uso de la ubicación">
                <b-form-select
                  v-model="formLocation.type_usage"
                  :options="typeOptions"
                  required>
                </b-form-select>
              </b-form-group>
            </b-col>
            <b-col lg="6" cols="12">
              <b-form-group
                label="Código de Barras"
                description="Código de barras de la ubicación">
                <b-form-input
                  v-model="formLocation.barcode"
                  placeholder="Ej: LOC001">
                </b-form-input>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col lg="6" cols="12">
              <b-form-group
                label="Ubicación Padre"
                description="Ubicación padre (opcional)">
                <vu-select
                  v-model="formLocation.location_id"
                  :options="parentLocationOptions"
                  :reduce="(x) => x.id"
                  label="complete_name"
                  placeholder="Seleccionar ubicación padre...">
                </vu-select>
              </b-form-group>
            </b-col>
            <b-col lg="6" cols="12">
              <b-form-group
                label="Sucursal"
                description="Sucursal asociada (opcional)">
                <b-form-input
                  v-model.number="formLocation.sucursal_id"
                  type="number"
                  placeholder="ID de sucursal">
                </b-form-input>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12">
              <b-form-group
                label="Comentario"
                description="Notas adicionales sobre la ubicación">
                <b-form-textarea
                  v-model="formLocation.comment"
                  rows="3"
                  placeholder="Ej: Ubicación para productos terminados">
                </b-form-textarea>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col lg="4" cols="12">
              <b-form-checkbox v-model="formLocation.is_scrap_location">
                Ubicación de Chatarra
              </b-form-checkbox>
            </b-col>
            <b-col lg="4" cols="12">
              <b-form-checkbox v-model="formLocation.is_return_location">
                Ubicación de Devoluciones
              </b-form-checkbox>
            </b-col>
            <b-col lg="4" cols="12">
              <b-form-checkbox v-model="formLocation.is_replenish_location">
                Ubicación de Reabastecimiento
              </b-form-checkbox>
            </b-col>
          </b-row>

          <b-row class="mt-3">
            <b-col lg="12" cols="12">
              <b-form-checkbox v-model="formLocation.status">
                Activo
              </b-form-checkbox>
            </b-col>
          </b-row>

          <b-row class="mt-3">
            <b-col>
              <b-button
                type="submit"
                variant="success"
                class="mr-2"
                :disabled="saving">
                <b-spinner small v-if="saving"></b-spinner>
                {{ saving ? 'Guardando...' : 'Guardar' }}
              </b-button>
              <b-button
                variant="secondary"
                @click="$bvModal.hide('modal-location')">
                Cancelar
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-modal>
    </b-container>
  </div>
</template>

<script>
import axios from "axios";
import Nav from "../components/Nav.vue";

export default {
  name: "StockLocations",
  components: { Nav },
  data() {
    return {
      search: "",
      page: 1,
      byPage: 20,
      loading: false,
      saving: false,
      isEditMode: false,
      locations: [],
      formLocation: this.getEmptyLocation(),
      locationFields: [
        { key: "id", label: "ID", sortable: true },
        { key: "name", label: "Nombre", sortable: true },
        { key: "complete_name", label: "Nombre Completo", sortable: true },
        { key: "type_usage", label: "Tipo", sortable: true },
        { key: "barcode", label: "Código", sortable: true },
        { key: "status", label: "Estado", sortable: true },
        { key: "is_scrap_location", label: "Chatarra" },
        { key: "is_return_location", label: "Devolución" },
        { key: "is_replenish_location", label: "Reabastecimiento" },
        { key: "buttons", label: "Acciones" }
      ],
      typeOptions: [
        { value: "supplier", text: "Proveedor" },
        { value: "view", text: "Vista" },
        { value: "internal", text: "Interno" },
        { value: "customer", text: "Cliente" },
        { value: "inventory", text: "Inventario" },
        { value: "production", text: "Producción" },
        { value: "transit", text: "Tránsito" }
      ]
    };
  },
  computed: {
    filteredLocations() {
      if (!this.search) return this.locations;
      const searchLower = this.search.toLowerCase();
      return this.locations.filter(loc => {
        return (
          loc.name?.toLowerCase().includes(searchLower) ||
          loc.complete_name?.toLowerCase().includes(searchLower) ||
          loc.barcode?.toLowerCase().includes(searchLower) ||
          loc.comment?.toLowerCase().includes(searchLower)
        );
      });
    },
    parentLocationOptions() {
      // Exclude the current location from parent options when editing
      if (this.isEditMode && this.formLocation.id) {
        return this.locations.filter(loc => loc.id !== this.formLocation.id);
      }
      return this.locations;
    }
  },
  mounted() {
    this.getLocations();
  },
  methods: {
    getEmptyLocation() {
      return {
        id: null,
        location_id: null,
        name: "",
        complete_name: "",
        type_usage: "internal",
        parent_path: "",
        barcode: null,
        comment: "",
        is_scrap_location: false,
        is_return_location: false,
        is_replenish_location: false,
        status: true,
        sucursal_id: null,
        company_id: null
      };
    },
    getLocations() {
      let that = this;
      this.loading = true;
      axios.get("/stocklocations")
        .then(res => {
          that.locations = res.data;
          that.loading = false;
        })
        .catch(err => {
          that.loading = false;
          that.makeToast("Error", err.response?.data || err.message, "danger");
        });
    },
    openCreateModal() {
      this.isEditMode = false;
      this.formLocation = this.getEmptyLocation();
      this.$bvModal.show('modal-location');
    },
    openEditModal(location) {
      this.isEditMode = true;
      this.formLocation = { ...location };
      this.$bvModal.show('modal-location');
    },
    saveLocation() {
      if (this.isEditMode) {
        this.updateLocation();
      } else {
        this.createLocation();
      }
    },
    createLocation() {
      let that = this;
      this.saving = true;
      axios.post("/stocklocations", this.formLocation)
        .then(() => {
          that.saving = false;
          that.makeToast("Éxito", "Ubicación creada correctamente", "success");
          that.$bvModal.hide('modal-location');
          that.getLocations();
        })
        .catch(err => {
          that.saving = false;
          that.makeToast("Error", err.response?.data || err.message, "danger");
        });
    },
    updateLocation() {
      let that = this;
      this.saving = true;
      axios.put("/stocklocations/" + this.formLocation.id, this.formLocation)
        .then(() => {
          that.saving = false;
          that.makeToast("Éxito", "Ubicación actualizada correctamente", "success");
          that.$bvModal.hide('modal-location');
          that.getLocations();
        })
        .catch(err => {
          that.saving = false;
          that.makeToast("Error", err.response?.data || err.message, "danger");
        });
    },
    confirmDelete(location) {
      this.$bvModal.msgBoxConfirm(
        `¿Está seguro de eliminar la ubicación "${location.name}"? Esta acción no se puede deshacer.`,
        {
          title: 'Confirmar Eliminación',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Eliminar',
          cancelTitle: 'Cancelar',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      )
      .then(value => {
        if (value) {
          this.deleteLocation(location.id);
        }
      })
      .catch(err => {
        console.error(err);
      });
    },
    deleteLocation(locationId) {
      let that = this;
      axios.delete("/stocklocations/" + locationId)
        .then(() => {
          that.makeToast("Éxito", "Ubicación eliminada correctamente", "success");
          that.getLocations();
        })
        .catch(err => {
          that.makeToast("Error", err.response?.data || err.message, "danger");
        });
    },
    resetForm() {
      this.formLocation = this.getEmptyLocation();
      this.isEditMode = false;
    },
    getTypeVariant(type) {
      const variants = {
        supplier: "secondary",
        view: "info",
        internal: "primary",
        customer: "success",
        inventory: "warning",
        production: "dark",
        transit: "light"
      };
      return variants[type] || "secondary";
    },
    getTypeLabel(type) {
      const labels = {
        supplier: "Proveedor",
        view: "Vista",
        internal: "Interno",
        customer: "Cliente",
        inventory: "Inventario",
        production: "Producción",
        transit: "Tránsito"
      };
      return labels[type] || type;
    },
    makeToast(title, content, variant) {
      this.$bvToast.toast(content, {
        title,
        variant,
        autoHideDelay: 5000,
        appendToast: true,
      });
    }
  }
};
</script>

<style scoped>
</style>
